import React, { useContext, useState, useEffect } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import { gql } from "apollo-boost";
import { useHistory } from "react-router-dom";
import { useQuery, useMutation } from "@apollo/react-hooks";
import { GET_BOOST_PAST_DUE_AMOUNT } from "components/Pages/Boost/mutations";
import {
  UPDATE_BOOST_PAYMENT_METHOD,
  BOOST_PRODUCT_SETTINGS_GQL,
} from "utils/mutations";
import RadioButtons from "components/common/RadioButtons";
import { Loading, MobileContext } from "components/common";
import SetupForm from "./SetupForm";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);

const INTENT_QUERY = gql`
  query StripeSetupIntent {
    getStripeSetupIntent {
      status
      clientSecret
    }
    getBoostPaymentMethod {
      status
      paymentMethods {
        id
        name
        accountType
        default
        type
      }
    }
  }
`;

const refetchAfterPayment = [
  {
    query: BOOST_PRODUCT_SETTINGS_GQL,
  },
  {
    query: GET_BOOST_PAST_DUE_AMOUNT,
  },
];

const toPaymentIcon = ({ type, brand, accountType }) => {
  const cardBrands = {
    amex: "fa-brands fa-cc-amex",
    discover: "fa-brands fa-cc-discover",
    mastercard: "fa-brands fa-cc-mastercard",
    visa: "fa-brands fa-cc-visa",
    default: "fa-regular fa-credit-card",
  };

  if (type === "card") {
    return (
      <i
        className={`text-2xl mr-2 ${cardBrands[brand] || cardBrands.default}`}
      />
    );
  }
  if (type === "us_bank_account") {
    return <i className="text-2xl mr-2 fa-solid fa-building-columns" />;
  }

  // link, cashapp, klarna use the default icon
  return <i className="text-2xl mr-2 fa-solid fa-money-check-dollar" />;
};

const PaymentMethodItem = ({ paymentMethod, disabled }) => {
  const { name = "", accountType = "", type = "" } = paymentMethod?.element || {};
  const brand = name?.split(" ")[0]; // the backend prefixes brand to the name

  return (
    <div
      className={`flex flex-row items-center ${
        disabled ? "text-gray-400 cursor-not-allowed" : ""
      }`}
      key={`${name}}`}
    >
      {toPaymentIcon({ type, brand, accountType })}
      <div className="">{name}</div>
      {!!paymentMethod?.element?.default && (
        <span
          className={`ml-2 px-2 bg-black text-white text-sm rounded-full ${
            disabled && "bg-gray-300"
          }`}
        >
          default
        </span>
      )}
    </div>
  );
};

const AddNewPaymentOption = ({
  stripe,
  options,
  isMobile,
  pastDue,
  handleAdding,
}) => {
  const [showAdd, setShowAdd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [stripeLoaded, setStripeLoaded] = useState(false);

  useEffect(() => {
    handleAdding?.(showAdd);
  }, [showAdd]);

  const toggleShowAdd = () => {
    if (showAdd) {
      setStripeLoaded(false);
    } else {
      setLoading(true);
    }
    setShowAdd(!showAdd);
  };

  const handleOnReady = () => {
    setLoading(false);
    setStripeLoaded(true);
  };

  return (
    <div className="relative">
      {showAdd && !loading ? (
        <button
          type="button"
          className={`${
            stripeLoaded ? "visible" : "hidden"
          } my-4 outline-none focus:outline-none text-red-500 hover:text-red-600 absolute right-0 z-10 fadeIn flex items-center`}
          onClick={toggleShowAdd}
        >
          <span className="mr-2">cancel</span>
          <i className="fa fa-circle-xmark mr-4" />
        </button>
      ) : (
        <button
          type="button"
          disabled={loading}
          className={`my-4 outline-none focus:outline-none ${
            loading ? "" : "hover:text-boost-primary-purple"
          } flex items-center`}
          onClick={toggleShowAdd}
          style={loading ? { background: "none" } : {}}
        >
          {loading ? (
            <i className="fas fa-circle-notch fa-spin mr-2" />
          ) : (
            <i className="fa fa-circle-plus mr-2" />
          )}
          <span>add a new payment method</span>
        </button>
      )}
      {!!showAdd && options?.clientSecret && (
        <Elements stripe={stripe} options={options}>
          <SetupForm
            isMobile={isMobile}
            pastDue={pastDue}
            onReady={handleOnReady}
          />
        </Elements>
      )}
    </div>
  );
};

const ManageStripePayments = ({ user }) => {
  const { pastDueAmount = 0 } = user?.boost || {};
  const [selectedPaymentOption, setSelectedPaymentOption] = useState();
  const [errorMessage, setErrorMessage] = useState("");
  const [SuccessMessage, setSuccessMessage] = useState("");
  const { isMobile } = useContext(MobileContext);
  const [isLoading, setIsLoading] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const history = useHistory();

  const {
    data: { getStripeSetupIntent = {}, getBoostPaymentMethod = {} } = {},
    loading,
    refetch: reloadPaymentMethods,
  } = useQuery(INTENT_QUERY);
  const { clientSecret } = getStripeSetupIntent || {};
  let { paymentMethods = [] } = getBoostPaymentMethod || {};
  paymentMethods = paymentMethods.map((pm) => {
    if (pm.type !== "card") {
      pm.disabled = true;
    }

    return pm;
  });

  const [updatePaymentMethod] = useMutation(UPDATE_BOOST_PAYMENT_METHOD);

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const setupIntentId = queryParams.get("setup_intent");
    const setAsDefault = queryParams.get("setDefault");

    if (setupIntentId) {
      updatePaymentMethod({
        variables: {
          setupIntentId,
          ...(setAsDefault && {
            setAsDefault: true,
          }),
          makePayment: !!pastDueAmount,
        },
        ...(!!pastDueAmount && { refetchQueries: refetchAfterPayment }),
      })
        .then(({ data: { updateBoostPaymentMethod = {} } = {} } = {}) => {
          queryParams.delete("setup_intent");
          queryParams.delete("setDefault");
          queryParams.delete("setup_intent_client_secret");
          history.replace({ search: queryParams.toString() });
          if (updateBoostPaymentMethod?.status === "ERROR") {
            setErrorMessage(updateBoostPaymentMethod?.message);
          } else {
            setSuccessMessage(updateBoostPaymentMethod?.message);
          }
        })
        .catch(() => {
          setErrorMessage("Something went wrong, please try again");
        })
        .finally(() => {
          reloadPaymentMethods();
        });
    }
  }, []);

  if (loading || isLoading) {
    return <Loading boost />;
  }

  if (!selectedPaymentOption) {
    const defaultPm = paymentMethods.find((pm) => pm.default);
    if (defaultPm) {
      defaultPm.selected = true;
      setSelectedPaymentOption(defaultPm);
    }
  }

  const callModifyPayment = () => {
    if (!selectedPaymentOption) {
      return;
    }
    setIsLoading(true);
    setErrorMessage("");
    setSuccessMessage("");

    updatePaymentMethod({
      variables: {
        paymentMethodId: selectedPaymentOption.id,
        setAsDefault: true,
        makePayment: !!pastDueAmount,
      },
      ...(!!pastDueAmount && { refetchQueries: refetchAfterPayment }),
    })
      .then(({ data: { updateBoostPaymentMethod = {} } = {} } = {}) => {
        reloadPaymentMethods().then(() => {
          if (updateBoostPaymentMethod?.status === "ERROR") {
            setErrorMessage(updateBoostPaymentMethod?.message);
          } else {
            setSuccessMessage(updateBoostPaymentMethod?.message);
          }
          setSelectedPaymentOption();
          setIsLoading(false);
        });
      })
      .catch(() => {
        setErrorMessage("Something went wrong, please try again.");
        setIsLoading(false);
      });
  };

  return (
    <div className={`mt-4 w-full ${isMobile ? "flex flex-col" : ""}`}>
      {!!pastDueAmount && (
        <p className="text-red-500 mb-2">
          Your Boost membership payment of ${(pastDueAmount / 100).toFixed(2)}{" "}
          is past due. Please make a payment.
        </p>
      )}
      {!paymentMethods?.length && (
        <div>You currently have no payment methods added</div>
      )}
      <RadioButtons
        disabled={isAdding}
        boost
        labelContainerClassName="flex"
        elements={paymentMethods}
        didSelect={(el) => {
          if (selectedPaymentOption && el.id !== selectedPaymentOption.id) {
            selectedPaymentOption.selected = false;
          }
          setSelectedPaymentOption(el);
        }}
        component={(pm) => (
          <PaymentMethodItem
            disabled={isAdding || !pm?.element?.id}
            paymentMethod={pm}
            isMobile={isMobile}
          />
        )}
        labelClassnames="flex items-center"
      />
      <AddNewPaymentOption
        stripe={stripePromise}
        options={{ clientSecret }}
        isMobile={isMobile}
        pastDue={pastDueAmount}
        handleAdding={(adding) => setIsAdding(adding)}
      />
      {errorMessage && <p className="text-red-500">{errorMessage}</p>}
      {SuccessMessage && <p className="text-green-500">{SuccessMessage}</p>}
      {!isAdding && !!paymentMethods?.length && (
        <button
          type="button"
          className={`btn my-4 ${
            isMobile ? "w-3/4 self-center" : ""
          } text-base bg-boost-primary-purple border-none`}
          disabled={!selectedPaymentOption || (selectedPaymentOption?.default && !pastDueAmount)}
          onClick={() => {
            callModifyPayment();
          }}
        >
          {pastDueAmount ? "Submit Payment" : "Use as Default"}
        </button>
      )}
    </div>
  );
};

export default ManageStripePayments;
